<template>
  <router-link
    :to="props.attractionUrl"
    class="sight-wrap"
    @click="sightClickEvent"
  >
    <div class="sight">
      <ui-image
        v-if="haveImage"
        :photo="props.attraction.images?.cover"
        :alt="title"
        class="sight__img"
        fit="cover"
      />
      <div class="sight__info">
        <div class="sight__title">
          {{ props.attraction.name }}
        </div>
        <span class="sight__count">
          {{ attractionDataLabel }}
        </span>
      </div>
    </div>
  </router-link>
</template>

<script setup lang="ts">
  import { UiImage } from '@experience/core-image-ui';
  import type { CityTag, RegionTag } from '@/shared/api/experience';
  import { toursCount } from '@/shared/consts';
  import { choosePlural } from '@experience/shared';
  import ru from '../../i18n/ru.json';
  import { useI18n } from '@experience/i18n';
  import type { ContextGeoSubject } from '@/shared/types/geography';
  import { trackEvent } from '@/entities/sogu';

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    attraction: CityTag | RegionTag;
    attractionUrl: string;
    defaultSoguFields: ContextGeoSubject;
  }

  const props = defineProps<Props>();

  const title = computed(() => t('attraction.title', { attractionName: props.attraction.name }));

  const haveImage = computed(() => Boolean(props.attraction.images?.cover));

  const sightClickEvent = () => {
    trackEvent('listing:sights.click', { ...props.defaultSoguFields.soguStamp, tag_id: props.attraction.id });
  };

  const attractionDataLabel = computed(() => {
    const count = props.attraction?.counters?.tours_count;
    const typeOfPluralized = toursCount.items_pluralized;

    return count ? `${count} ${choosePlural(count, typeOfPluralized)}` : '';
  });
</script>
<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .sight-wrap {
    display: block;
    height: 263px;
    text-decoration: none;
    transition: all 0.2s;
    transform: none;

    &:hover {
      .sight__img {
        transition: all 0.2s ease-out;
        transform: scale(110%);

        @include adaptivity.tablet {
          transform: none;
        }
      }
    }

    @include adaptivity.mobile {
      transform: none !important;
    }
  }

  .sight {
    position: relative;
    display: flex;
    align-items: flex-end;
    overflow: hidden;
    height: 100%;
    border-radius: 8px;
    text-decoration: none;

    &:before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 3;
      border-radius: 8px;
      background: linear-gradient(0.33deg, var(--global-color-neutral-black) 0.27%, transparent 46.03%);
    }

    @include adaptivity.mobile {
      margin: 0;
    }
  }

  .sight__img {
    position: absolute !important;
    z-index: 2;
    width: 100% !important;
    max-width: 100% !important;
    height: 100% !important;
    margin: auto;
    border-radius: 8px;
    transition: all 0.2s;
    transform: none;

    :deep(img) {
      object-fit: cover;
      object-position: left;
      width: 100% !important;
      max-width: 100% !important;
      height: 100% !important;
    }

    &[title] {
      pointer-events: none;
    }
  }

  .sight__info {
    position: relative;
    z-index: 4;
    box-sizing: border-box;
    width: 100%;
    margin: 0 0 21px;
    padding: 0 16px;
    border-radius: 0 0 8px 8px;
    color: var(--alias-color-text-inverse);
    text-align: center;
    transition: all 0.2s;
  }

  .sight__title {
    margin: 0;

    @include typography.base-400-300-semi;
  }

  .sight__count {
    @include typography.base-300-300-regular;
  }
</style>
